import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Megaphone } from 'lucide-react';
import { DialogShellLayout, DialogShellSection, Label, Switch } from '@bliks-io/ui';
import {
  PARTNER_MATERIAL_LOCALES,
  PARTNER_NEWS_IMAGE_SLOTS,
  type PartnerMaterialLocale,
  type PartnerNewsAdmin,
  type PartnerNewsImageSlot
} from '@bliks-io/contracts';
import { FormInput, FormTextarea } from '@/components/form/rhf';
import { PartnerNewsStory } from '@/components/partner-content/PartnerNewsStory';
import { PARTNER_MATERIAL_LOCALE_NAMES } from '@/lib/partner-material-text';
import { ContentEditorDialog } from '@/pages/admin/partner-content/components/ContentEditorDialog';
import {
  LocaleNav,
  type LocaleCompleteness
} from '@/pages/admin/partner-content/components/LocaleNav';
import { NewsImageSlot } from './NewsImageSlot';
import {
  NO_PENDING_IMAGES,
  NewsFormSchema,
  newsCompleteness,
  previewImageUrl,
  toNewsForm,
  toNewsPreview,
  type NewsForm,
  type PendingImages
} from './newsForm';
import { useSaveNews } from './useSaveNews';

type ActionName = 'primaryAction' | 'secondaryAction';

const ACTION_KEYS: Record<ActionName, string> = {
  primaryAction: 'partnerContent.news.primaryAction',
  secondaryAction: 'partnerContent.news.secondaryAction'
};

interface NewsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The news being edited, or null to write a new one. */
  news: PartnerNewsAdmin | null;
}

/** Mount with a fresh `key` per opening, so one draft never leaks into the next. */
export function NewsDialog({ open, onOpenChange, news }: NewsDialogProps) {
  const { t } = useTranslation();
  const { save, isSaving } = useSaveNews();
  const [locale, setLocale] = useState<PartnerMaterialLocale>('de');
  const [pending, setPending] = useState<PendingImages>(NO_PENDING_IMAGES);
  const [previews, setPreviews] = useState<Partial<Record<PartnerNewsImageSlot, string>>>({});
  // Fixed per opening, so the preview of a new entry does not tick while typing.
  const [previewDate] = useState(() => news?.publishedAt ?? new Date().toISOString());

  const form = useForm<NewsForm>({
    resolver: zodResolver(NewsFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: toNewsForm(news)
  });
  const values = useWatch({ control: form.control }) as NewsForm;
  const pinned = useWatch({ control: form.control, name: 'pinned' });

  const completeness = Object.fromEntries(
    PARTNER_MATERIAL_LOCALES.map((value) => [value, newsCompleteness(values, value)])
  ) as Record<PartnerMaterialLocale, LocaleCompleteness>;
  const language = PARTNER_MATERIAL_LOCALE_NAMES[locale];
  const shownImages = Object.fromEntries(
    PARTNER_NEWS_IMAGE_SLOTS.map((slot) => [slot, previewImageUrl(news, pending, previews, slot)])
  ) as Partial<Record<PartnerNewsImageSlot, string>>;

  const pickImage = (slot: PartnerNewsImageSlot, file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const url = reader.result;
        setPreviews((current) => ({ ...current, [slot]: url }));
      }
    };
    reader.readAsDataURL(file);
    setPending((current) => ({
      uploads: { ...current.uploads, [slot]: file },
      removals: current.removals.filter((removed) => removed !== slot)
    }));
  };
  const removeImage = (slot: PartnerNewsImageSlot) =>
    setPending((current) => ({ ...current, removals: [...current.removals, slot] }));
  const undoImage = (slot: PartnerNewsImageSlot) =>
    setPending((current) => {
      const { [slot]: _undone, ...uploads } = current.uploads;
      return { uploads, removals: current.removals.filter((removed) => removed !== slot) };
    });

  const submit = (publish: boolean) =>
    form.handleSubmit(
      async (submitted) => {
        try {
          await save({ existing: news, values: submitted, pending, publish });
          toast.success(
            t(publish ? 'partnerContent.toast.published' : 'partnerContent.toast.saved')
          );
          onOpenChange(false);
        } catch {
          toast.error(t('partnerContent.toast.saveFailed'));
        }
      },
      // Title, text and button labels are required in German, so a failed check shows that language.
      () => setLocale('de')
    )();

  return (
    <ContentEditorDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<Megaphone className="h-5 w-5" />}
      title={t(news ? 'partnerContent.news.editTitle' : 'partnerContent.news.createTitle')}
      subtitle={t('partnerContent.news.dialogDescription')}
      published={news ? news.published : null}
      isSaving={isSaving}
      onSave={submit}
    >
      <form noValidate onSubmit={(event) => event.preventDefault()} className="flex flex-col gap-6">
        <DialogShellSection
          title={t('partnerContent.news.preview')}
          hint={t('partnerContent.news.previewHint', { language })}
        >
          <PartnerNewsStory
            slides={[toNewsPreview(values, shownImages, previewDate)]}
            locale={locale}
            autoAdvance={false}
          />
        </DialogShellSection>

        <DialogShellSection title={t('partnerContent.news.images.title')}>
          <div className="grid grid-cols-2 gap-4">
            {PARTNER_NEWS_IMAGE_SLOTS.map((slot) => (
              <NewsImageSlot
                key={slot}
                slot={slot}
                previewUrl={shownImages[slot]}
                isPending={Boolean(pending.uploads[slot])}
                hasStored={Boolean(news?.[slot]) && pending.removals.includes(slot)}
                onPick={(file) => pickImage(slot, file)}
                onRemove={() => removeImage(slot)}
                onUndo={() => undoImage(slot)}
              />
            ))}
          </div>
        </DialogShellSection>

        <DialogShellSection title={t('partnerContent.news.links')}>
          <div className="grid grid-cols-2 gap-4">
            {(['primaryAction', 'secondaryAction'] as const).map((name) => (
              <FormInput
                key={name}
                control={form.control}
                name={`${name}.url`}
                type="url"
                label={t(ACTION_KEYS[name])}
                placeholder="https://"
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="partner-news-pinned"
              checked={pinned}
              onCheckedChange={(checked) => form.setValue('pinned', checked)}
            />
            <Label htmlFor="partner-news-pinned">{t('partnerContent.news.pinned')}</Label>
          </div>
        </DialogShellSection>

        <DialogShellSection
          title={t('partnerContent.languages.contentTitle')}
          hint={t('partnerContent.languages.hint')}
        >
          <DialogShellLayout
            sidebarWidth={180}
            sidebar={<LocaleNav active={locale} onSelect={setLocale} completeness={completeness} />}
          >
            {/* Keyed so each language mounts its own fields, not a re-bound copy. */}
            <div key={locale} className="flex flex-col gap-4">
              <FormInput
                control={form.control}
                name={`eyebrow.${locale}`}
                label={t('partnerContent.news.eyebrowIn', { language })}
              />
              <FormInput
                control={form.control}
                name={`title.${locale}`}
                label={t('partnerContent.fields.titleIn', { language })}
                required={locale === 'de'}
              />
              <FormTextarea
                control={form.control}
                name={`body.${locale}`}
                label={t('partnerContent.news.bodyIn', { language })}
                required={locale === 'de'}
                rows={4}
              />
              <div className="grid grid-cols-2 gap-4">
                {(['primaryAction', 'secondaryAction'] as const).map((name) => (
                  <FormInput
                    key={name}
                    control={form.control}
                    name={`${name}.label.${locale}`}
                    label={t('partnerContent.news.actionLabelIn', {
                      action: t(ACTION_KEYS[name]),
                      language
                    })}
                  />
                ))}
              </div>
              <FormInput
                control={form.control}
                name={`footnote.${locale}`}
                label={t('partnerContent.news.footnoteIn', { language })}
              />
            </div>
          </DialogShellLayout>
        </DialogShellSection>
      </form>
    </ContentEditorDialog>
  );
}
